import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMutation } from './mutation.js';
import type { Bilingue, Langue } from '@artosera/shared';
import type { NomPolice, NomTheme, Rubrique, SiteData } from '@artosera/site-renderer';
import { api, type AppelApi } from './client.js';
import { cleTableauDeBord } from './tableauDeBord.js';
import { intervalleDeSuivi } from './suiviPublication.js';
import { appelDeRequete, cleMetier, dansLaPortee } from './cles.js';

/**
 * Réglages, pages, redirections, contrat de données et publication du site —
 * `/v1/g/:slug/site` (`docs/dev/api.md` § 15.6 à § 15.10, `docs/dev/publication.md`
 * § 6, S6-f). Les formes suivent `apps/api/src/site-reglages/*` et
 * `apps/api/src/publication/*` au champ près (même règle que `api/expositions.ts`).
 *
 * Deux champs ne se PATCHent jamais : `dernierePublication` et
 * `versionPubliee` appartiennent à la publication (§ 15.6).
 */

// ── Réglages ─────────────────────────────────────────────────────────────────

export interface ChartePresentee {
  readonly logotypeMediaId: string | null;
  readonly logotypeHauteur: number | null;
  readonly couleurPrincipale: string;
  readonly couleurSecondaire: string;
  readonly policeTitres: NomPolice;
  readonly policeTexte: NomPolice;
}

export interface PolicesAdmises {
  readonly titres: readonly NomPolice[];
  readonly texte: readonly NomPolice[];
}

export interface EntreeMenuSite {
  readonly rubrique: Rubrique;
  readonly libelle?: Bilingue;
}

export interface MenusSite {
  readonly principal: readonly EntreeMenuSite[];
  readonly pied: readonly EntreeMenuSite[];
}

export type TypeBlocAccueil =
  | 'prise-rendez-vous'
  | 'boutique'
  | 'exposition-en-cours'
  | 'oeuvres'
  | 'artistes'
  | 'agenda'
  | 'evenements'
  | 'actualites'
  | 'newsletter'
  | 'texte';

export interface BlocAccueilSite {
  readonly type: TypeBlocAccueil;
  readonly limite?: number;
  readonly titre?: Bilingue;
  readonly corps?: Bilingue;
}

export interface OptionsSite {
  readonly oeuvresDisponibles: boolean;
  readonly formulaireContact: boolean;
  readonly newsletter: boolean;
}

export type UsageAdresseSite = 'principale' | 'secondaire' | 'bureau' | 'reserve';

export interface AdressePubliqueSite {
  readonly usage: UsageAdresseSite;
  readonly libelle?: Bilingue;
  readonly rue: string;
  readonly complement?: string;
  readonly codePostal: string;
  readonly ville: string;
  readonly pays: string;
  readonly latitude?: number;
  readonly longitude?: number;
  readonly planUrl?: string;
  readonly telephone?: string;
}

export type JourSite =
  'lundi' | 'mardi' | 'mercredi' | 'jeudi' | 'vendredi' | 'samedi' | 'dimanche';

export interface CreneauSite {
  readonly ouverture: string;
  readonly fermeture: string;
}

export interface PlageHoraireSite {
  readonly du: JourSite;
  readonly au: JourSite;
  readonly creneaux: readonly CreneauSite[];
}

export interface HorairesSite {
  readonly plages: readonly PlageHoraireSite[];
  readonly note?: Bilingue;
}

export type ReseauSite = 'instagram' | 'artsy' | 'facebook' | 'linkedin' | 'vimeo' | 'youtube';

export interface LienReseauSite {
  readonly reseau: ReseauSite;
  readonly libelle: string;
  readonly url: string;
}

export interface IdentiteSite {
  readonly baseline: Bilingue | null;
  readonly description: Bilingue;
  readonly email: string;
  readonly telephone: string | null;
  readonly adresses: readonly AdressePubliqueSite[];
  readonly horaires: HorairesSite;
  readonly reseaux: readonly LienReseauSite[];
}

export interface RedirectionSite {
  readonly de: string;
  readonly vers: string;
  readonly code: number;
  readonly poseeLe: string;
}

export interface ReglagesSite {
  readonly theme: NomTheme;
  readonly charte: ChartePresentee;
  /** Les polices que CE thème admet : deux listes déroulantes, sans deviner (§ 15.6). */
  readonly policesAdmises: PolicesAdmises;
  readonly accueil: readonly BlocAccueilSite[];
  readonly langues: readonly Langue[];
  readonly langueParDefaut: Langue;
  readonly menus: MenusSite;
  readonly options: OptionsSite;
  readonly identite: IdentiteSite;
  readonly mentionArtosera: boolean;
  /** Faux quand le palier ne permet pas de la retirer : la régie grise la case. */
  readonly mentionDesactivable: boolean;
  readonly redirections: readonly RedirectionSite[];
  readonly dernierePublication: string | null;
  readonly versionPubliee: number;
  readonly modifieLe: string;
}

export interface ReponseSite {
  readonly site: ReglagesSite;
  /** Domaine principal actif, sinon le sous-domaine de repli (D11, § 15.6). */
  readonly domaine: string;
}

export interface CorpsCharte {
  logotypeMediaId?: string | null;
  logotypeHauteur?: number | null;
  couleurPrincipale?: string;
  couleurSecondaire?: string;
  policeTitres?: NomPolice;
  policeTexte?: NomPolice;
}

export interface CorpsIdentite {
  baseline?: Bilingue | null;
  description?: Bilingue;
  email?: string;
  telephone?: string | null;
  adresses?: readonly AdressePubliqueSite[];
  horaires?: HorairesSite;
  reseaux?: readonly LienReseauSite[];
}

export interface CorpsMiseAJourSite {
  theme?: NomTheme;
  charte?: CorpsCharte;
  accueil?: readonly BlocAccueilSite[];
  langues?: readonly Langue[];
  langueParDefaut?: Langue;
  menus?: {
    principal?: readonly EntreeMenuSite[];
    pied?: readonly EntreeMenuSite[];
  };
  options?: Partial<OptionsSite>;
  identite?: CorpsIdentite;
  mentionArtosera?: boolean;
}

// ── Redirections ─────────────────────────────────────────────────────────────

export interface CorpsRedirection {
  de: string;
  vers: string;
  code?: 301;
}

export interface ReponseRedirections {
  readonly redirections: readonly RedirectionSite[];
}

// ── Pages fixes ──────────────────────────────────────────────────────────────

export type RubriquePage = 'la-galerie' | 'contact' | 'mentions' | 'confidentialite';

export const RUBRIQUES_PAGE: readonly RubriquePage[] = [
  'la-galerie',
  'contact',
  'mentions',
  'confidentialite',
];

export interface MembreEquipeSite {
  readonly nom: string;
  readonly role: Bilingue;
  readonly email?: string;
}

export interface PageSite {
  readonly id: string;
  readonly rubrique: RubriquePage | null;
  readonly cle: string;
  readonly slug: string;
  readonly titre: Bilingue;
  readonly contenu: Bilingue;
  readonly equipe: readonly MembreEquipeSite[];
  readonly appelNewsletter: Bilingue | null;
  readonly ordre: number;
  readonly visibleSurLeSite: boolean;
  readonly modifieLe: string;
}

export interface ReponsePages {
  readonly pages: readonly PageSite[];
}

export interface ReponsePage {
  readonly page: PageSite;
}

export interface CorpsMiseAJourPage {
  titre?: Bilingue;
  contenu?: Bilingue;
  equipe?: readonly MembreEquipeSite[];
  appelNewsletter?: Bilingue | null;
  visibleSurLeSite?: boolean;
}

// ── Contrat de données (aperçu, § 15.9) ───────────────────────────────────────

export interface OmissionSite {
  readonly quoi: 'artiste' | 'oeuvre' | 'exposition' | 'actualite';
  readonly id: string;
  readonly intitule: string;
  readonly raison: string;
}

export interface ReponseDonneesSite {
  readonly donnees: SiteData;
  readonly omissions: readonly OmissionSite[];
}

// ── Publication (S6-c, `docs/dev/publication.md` § 6) ─────────────────────────

export interface TravailPublication {
  readonly id: string;
  readonly type: string;
  readonly etat: string;
  readonly passeLe: string;
  /** Une publication regroupée attendait déjà : rien n'a été perdu, l'heure a avancé. */
  readonly regroupe: boolean;
}

export interface ReponsePublier {
  readonly travail: TravailPublication;
}

export type EtatPublication = 'en_cours' | 'reussie' | 'echouee';

export interface PublicationPresentee {
  readonly id: string;
  readonly version: number;
  readonly dossier: string | null;
  readonly etat: EtatPublication;
  readonly raison: string;
  readonly debuteLe: string;
  readonly termineLe: string | null;
  readonly dureeMs: number;
  readonly fichiers: number;
  readonly medias: number;
  readonly polices: number;
  readonly octets: number;
  readonly hotes: readonly string[];
  readonly erreur: string | null;
}

export interface ReponsePublications {
  readonly publications: readonly PublicationPresentee[];
}

// ── Client ───────────────────────────────────────────────────────────────────

export const siteApi = {
  obtenir: (galerie: string, appel?: AppelApi) =>
    api.get<ReponseSite>(`/g/${encodeURIComponent(galerie)}/site`, { galerie, ...appel }),
  modifier: (galerie: string, corps: CorpsMiseAJourSite, appel?: AppelApi) =>
    api.patch<{ site: ReglagesSite }>(`/g/${encodeURIComponent(galerie)}/site`, corps, {
      galerie,
      ...appel,
    }),
  mettreAJourRedirections: (
    galerie: string,
    redirections: readonly CorpsRedirection[],
    appel?: AppelApi,
  ) =>
    api.put<ReponseRedirections>(
      `/g/${encodeURIComponent(galerie)}/site/redirections`,
      { redirections },
      { galerie, ...appel },
    ),
  pages: (galerie: string, appel?: AppelApi) =>
    api.get<ReponsePages>(`/g/${encodeURIComponent(galerie)}/site/pages`, { galerie, ...appel }),
  modifierPage: (
    galerie: string,
    rubrique: RubriquePage,
    corps: CorpsMiseAJourPage,
    appel?: AppelApi,
  ) =>
    api.patch<ReponsePage>(`/g/${encodeURIComponent(galerie)}/site/pages/${rubrique}`, corps, {
      galerie,
      ...appel,
    }),
  donnees: (galerie: string, appel?: AppelApi) =>
    api.get<ReponseDonneesSite>(`/g/${encodeURIComponent(galerie)}/site/donnees`, {
      galerie,
      ...appel,
    }),
  publier: (galerie: string, raison?: string, appel?: AppelApi) =>
    api.post<ReponsePublier>(
      `/g/${encodeURIComponent(galerie)}/site/publier`,
      raison ? { raison } : {},
      { galerie, ...appel },
    ),
  publications: (galerie: string, limite = 20, appel?: AppelApi) =>
    api.get<ReponsePublications>(
      `/g/${encodeURIComponent(galerie)}/site/publications?limite=${String(limite)}`,
      { galerie, ...appel },
    ),
};

const cleSite = (galerie: string) => cleMetier(galerie, 'site', 'reglages');
const clePages = (galerie: string) => cleMetier(galerie, 'site', 'pages');
const cleDonnees = (galerie: string) => cleMetier(galerie, 'site', 'donnees');
const clePublications = (galerie: string) => cleMetier(galerie, 'site', 'publications');

export function useSite(galerie: string) {
  return useQuery({
    queryKey: cleSite(galerie),
    queryFn: (contexte) => siteApi.obtenir(galerie, appelDeRequete(contexte)),
  });
}

export function useModifierSite(galerie: string) {
  const client = useQueryClient();
  return useMutation(
    dansLaPortee({
      mutationFn: (corps: CorpsMiseAJourSite, appel) => siteApi.modifier(galerie, corps, appel),
      onSuccess: (donnees) => {
        client.setQueryData(cleSite(galerie), (precedent: ReponseSite | undefined) =>
          precedent ? { ...precedent, site: donnees.site } : precedent,
        );
        void client.invalidateQueries({ queryKey: cleDonnees(galerie) });
      },
    }),
  );
}

export function useMettreAJourRedirections(galerie: string) {
  const client = useQueryClient();
  return useMutation(
    dansLaPortee({
      mutationFn: (redirections: readonly CorpsRedirection[], appel) =>
        siteApi.mettreAJourRedirections(galerie, redirections, appel),
      onSuccess: (donnees) => {
        client.setQueryData(cleSite(galerie), (precedent: ReponseSite | undefined) =>
          precedent
            ? { ...precedent, site: { ...precedent.site, redirections: donnees.redirections } }
            : precedent,
        );
      },
    }),
  );
}

export function usePages(galerie: string) {
  return useQuery({
    queryKey: clePages(galerie),
    queryFn: (contexte) => siteApi.pages(galerie, appelDeRequete(contexte)),
    select: (reponse) => reponse.pages,
  });
}

export function useModifierPage(galerie: string) {
  const client = useQueryClient();
  return useMutation(
    dansLaPortee({
      mutationFn: (
        { rubrique, corps }: { rubrique: RubriquePage; corps: CorpsMiseAJourPage },
        appel,
      ) => siteApi.modifierPage(galerie, rubrique, corps, appel),
      onSuccess: () => {
        void client.invalidateQueries({ queryKey: clePages(galerie) });
      },
    }),
  );
}

/** Contrat de données courant (§ 15.9) : consommé par l'aperçu côté client. */
export function useDonneesSite(galerie: string, actif = true) {
  return useQuery({
    queryKey: cleDonnees(galerie),
    queryFn: (contexte) => siteApi.donnees(galerie, appelDeRequete(contexte)),
    enabled: actif,
    staleTime: 10_000,
  });
}

/** Dernier « Publier maintenant » de chaque galerie, pour suivre le travail enfilé. */
const lancements = new Map<string, number>();

function noterLancement(galerie: string): void {
  lancements.set(galerie, Date.now());
}

function intervalleDeLHistorique(
  galerie: string,
  publications: readonly { etat: EtatPublication; debuteLe: string }[],
): number | false {
  return intervalleDeSuivi(publications, lancements.get(galerie) ?? null, Date.now());
}

export function usePublications(galerie: string, limite = 20) {
  return useQuery({
    queryKey: [...clePublications(galerie), limite],
    queryFn: (contexte) => siteApi.publications(galerie, limite, appelDeRequete(contexte)),
    select: (reponse) => reponse.publications,
    // Rafraîchi à intervalle court après un déclenchement, le temps que le
    // worker publie (bouton « Publier maintenant », S6-f). En arrière-plan
    // aussi : la galerie change souvent d'onglet pendant qu'une publication
    // tourne, et doit retrouver l'état à jour en y revenant.
    refetchInterval: (requete) =>
      intervalleDeLHistorique(galerie, requete.state.data?.publications ?? []),
    refetchIntervalInBackground: true,
  });
}

export function usePublierMaintenant(galerie: string) {
  const client = useQueryClient();
  return useMutation(
    dansLaPortee({
      mutationFn: (raison: string | undefined, appel) => siteApi.publier(galerie, raison, appel),
      onSuccess: () => {
        noterLancement(galerie);
        void client.invalidateQueries({ queryKey: clePublications(galerie) });
        void client.invalidateQueries({ queryKey: cleSite(galerie) });
        // La tuile « Votre site » du tableau de bord lit la dernière
        // publication depuis l'agrégation (§ 20) : elle doit la refaire.
        void client.invalidateQueries({ queryKey: cleTableauDeBord(galerie) });
      },
    }),
  );
}

/**
 * Alias historique (S3-e/S3-f, tuile « Votre site » du tableau de bord) :
 * même mutation que `usePublierMaintenant`, sous le nom que
 * `TableauDeBordPage` attend. Un seul `api/site.ts` pour les deux écrans
 * (fusion `socle` du 22/09) — pas de doublon de logique, juste ce second nom.
 */
export const usePublierSite = usePublierMaintenant;
